import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Lock } from "lucide-react";
import CommunityLayout from "@/components/community/CommunityLayout";
import { BulletinCard, EmptyState, SectionTitle } from "@/components/community/Cards";
import { cApi, img, fmtDate, errMsg } from "@/lib/community";

export function BulletinList() {
  const [data, setData] = useState({ items: [], categories: [], locked_count: 0 });
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    cApi.get("/community/bulletin", { params: cat ? { category: cat } : {} })
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [cat]);

  const featured = data.items.find((i) => i.featured) || data.items[0];
  const rest = data.items.filter((i) => i !== featured);

  return (
    <CommunityLayout>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-10">
        <SectionTitle eyebrow="Weekly Bulletin" title="News from the" em="sisterhood" testId="bulletin-title">
          Founder notes, weekly inspiration, opportunities and everything happening this week at Jenova.
        </SectionTitle>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="bulletin-filters">
          <button className="jc-chip" data-active={!cat} onClick={() => setCat("")} data-testid="bulletin-filter-all">All</button>
          {data.categories.map((c) => (
            <button key={c} className="jc-chip" data-active={cat === c} onClick={() => setCat(c)} data-testid={`bulletin-filter-${c.toLowerCase().replace(/\W+/g, "-")}`}>{c}</button>
          ))}
        </div>
      </section>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24">
        {loading ? null : !featured ? (
          <EmptyState title="Nothing posted yet" testId="bulletin-empty">The first bulletin is on its way. Check back soon, sister.</EmptyState>
        ) : (
          <>
            <div className="grid md:grid-cols-12 gap-10" data-testid="bulletin-featured">
              <div className="md:col-span-8"><BulletinCard item={featured} large /></div>
              <div className="md:col-span-4 flex flex-col gap-10">
                {rest.slice(0, 2).map((i) => <BulletinCard key={i.id} item={i} />)}
              </div>
            </div>
            {rest.length > 2 && (
              <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
                {rest.slice(2).map((i) => <BulletinCard key={i.id} item={i} />)}
              </div>
            )}
          </>
        )}
        {data.locked_count > 0 && (
          <div className="mt-16 flex items-center gap-3 text-sm text-[#7A6A62]" data-testid="bulletin-locked-note">
            <Lock className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} />
            {data.locked_count} member-only announcement{data.locked_count > 1 ? "s" : ""} —{" "}
            <Link to="/become-a-jenova-girl" className="underline underline-offset-4">become a Jenova Girl</Link> to read.
          </div>
        )}
      </section>
    </CommunityLayout>
  );
}

export function BulletinDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    cApi.get(`/community/bulletin/${id}`).then(({ data }) => setA(data)).catch((e) => setError(errMsg(e, "Not found")));
  }, [id]);

  return (
    <CommunityLayout>
      <article className="max-w-3xl mx-auto px-6 pt-14 md:pt-20 pb-24" data-testid="bulletin-detail">
        <Link to="/bulletin" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7A6A62] hover:text-[#2B2522]" data-testid="bulletin-back-link">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.25} /> Weekly Bulletin
        </Link>
        {error && <EmptyState title={error} testId="bulletin-detail-error" />}
        {a && (
          <>
            <p className="jc-eyebrow mt-12">{a.category} · {fmtDate(a.date || a.published_at)}</p>
            <h1 className="jc-display text-4xl sm:text-5xl lg:text-6xl mt-5" data-testid="bulletin-detail-title">{a.title}</h1>
            <p className="mt-6 text-lg text-[#4A403B] leading-relaxed">{a.description}</p>
            {a.cover_image && (
              <div className="jc-card-img mt-12 aspect-[16/10]"><img src={img(a.cover_image)} alt="" className="w-full h-full object-cover" /></div>
            )}
            {a.body && <div className="mt-12 text-[15px] md:text-base leading-[1.9] text-[#4A403B] whitespace-pre-line">{a.body}</div>}
            {a.event_link && (
              <a href={a.event_link} className="jc-btn jc-btn-primary mt-12" data-testid="bulletin-event-link">
                View the experience <ExternalLink className="w-4 h-4" strokeWidth={1.25} />
              </a>
            )}
          </>
        )}
      </article>
    </CommunityLayout>
  );
}
